import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import ClubsEditor from "@/components/ClubsEditor";
import EmailPreferencesCard from "@/components/EmailPreferencesCard";
import PasswordInput from "@/components/PasswordInput";
import { roleLabel, roleTitle } from "@/lib/roleLabels";

const COURSE_BG =
  "/brand/proshop-apparel.jpg";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Email-change state — separate flow so it can't be accidentally fired by profile edit.
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState(null);

  if (!user) return null;
  const isMember = user.role === "member";

  const startEdit = () => {
    setForm({ name: user.name || "", phone: user.phone || "" });
    setEditing(true);
    setFeedback(null);
  };

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const payload = {};
      if (form.name.trim() && form.name !== user.name) payload.name = form.name.trim();
      if (form.phone !== (user.phone || "")) payload.phone = form.phone;
      if (Object.keys(payload).length === 0) {
        setEditing(false);
        return;
      }
      const { data } = await api.patch("/auth/me", payload);
      setUser(data);
      setFeedback({ type: "success", text: "Profile updated." });
      setEditing(false);
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const startEmailEdit = () => {
    setEmailForm({ new_email: "", current_password: "" });
    setEmailFeedback(null);
    setEmailEditing(true);
  };

  const cancelEmailEdit = () => {
    setEmailEditing(false);
    setEmailForm({ new_email: "", current_password: "" });
    setEmailFeedback(null);
  };

  const saveEmail = async () => {
    const next = emailForm.new_email.trim().toLowerCase();
    if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      setEmailFeedback({ type: "error", text: "Please enter a valid email address." });
      return;
    }
    if (!emailForm.current_password) {
      setEmailFeedback({ type: "error", text: "Please enter your current password." });
      return;
    }
    if (next === (user.email || "").toLowerCase()) {
      setEmailFeedback({ type: "error", text: "New email is the same as the current one." });
      return;
    }
    setEmailBusy(true);
    setEmailFeedback(null);
    try {
      const { data } = await api.patch("/auth/me/email", {
        new_email: next,
        current_password: emailForm.current_password,
      });
      setUser(data);
      setEmailFeedback({
        type: "success",
        text: "Email updated. A confirmation note has been sent to your previous address.",
      });
      setEmailForm({ new_email: "", current_password: "" });
      setEmailEditing(false);
      setTimeout(() => setEmailFeedback(null), 4000);
    } catch (err) {
      setEmailFeedback({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <section className="relative h-64 overflow-hidden border-b border-[#E5E3D8]">
        <img src={COURSE_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A422D]/60" />
        <div className="absolute inset-0 flex items-end max-w-7xl mx-auto px-6 sm:px-10 py-8">
          <div className="text-[#F9F8F4]">
            <div className="overline" style={{ color: "#D9C68A" }} data-testid="profile-role-eyebrow">
              {roleLabel(user)}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-none">{user.name}</h1>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-12 space-y-8">
        <div className="surface-card p-8" data-testid="profile-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="overline">Account</div>
              <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Profile Details</h2>
            </div>
            {!editing && (
              <button
                type="button"
                onClick={startEdit}
                data-testid="profile-edit"
                className="btn-secondary text-xs"
              >
                Edit Profile
              </button>
            )}
          </div>

          {feedback && (
            <div
              className={`mt-4 px-3 py-2 text-sm border ${
                feedback.type === "success"
                  ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                  : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
              }`}
              data-testid="profile-feedback"
            >
              {feedback.text}
            </div>
          )}

          {editing ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="label-elegant block mb-2">Full Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="profile-name-input"
                  className="input-field"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-elegant block mb-2">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="profile-phone-input"
                  className="input-field"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="sm:col-span-2 flex items-center justify-end gap-3">
                <button type="button" onClick={() => setEditing(false)} className="btn-secondary text-xs">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  data-testid="profile-save"
                  className="btn-primary"
                >
                  {busy ? "Saving…" : "Save Profile"}
                </button>
              </div>
            </div>
          ) : (
            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {[
                { label: "Name", value: user.name },
                { label: "Role", value: roleTitle(user), testid: "profile-role-value" },
                { label: "Phone", value: user.phone || "—" },
                { label: "Joined", value: user.created_at ? new Date(user.created_at).toLocaleDateString() : "—" },
              ].map((f) => (
                <div key={f.label} data-testid={f.testid}>
                  <dt className="label-elegant">{f.label}</dt>
                  <dd className="text-[#1A1A1A] mt-1">{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Email & Sign-In — its own card with a dedicated, password-protected flow */}
        {isMember && (
          <div className="surface-card p-8" data-testid="email-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="overline">Sign-In</div>
                <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Email Address</h2>
                <p className="text-sm text-[#4A4A4A] mt-1">
                  Used to sign in and to receive order updates from the pro shop.
                </p>
              </div>
              {!emailEditing && (
                <button
                  type="button"
                  onClick={startEmailEdit}
                  data-testid="email-edit"
                  className="btn-secondary text-xs"
                >
                  Edit Email
                </button>
              )}
            </div>

            {emailFeedback && (
              <div
                className={`mt-4 px-3 py-2 text-sm border ${
                  emailFeedback.type === "success"
                    ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                    : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
                }`}
                data-testid="email-feedback"
              >
                {emailFeedback.text}
              </div>
            )}

            {emailEditing ? (
              <div className="mt-6 grid grid-cols-1 gap-5">
                <div>
                  <label className="label-elegant block mb-2">Current Email</label>
                  <div className="text-[#1A1A1A]" data-testid="email-current">{user.email}</div>
                </div>
                <div>
                  <label className="label-elegant block mb-2" htmlFor="new-email-input">New Email</label>
                  <input
                    id="new-email-input"
                    type="email"
                    autoComplete="email"
                    value={emailForm.new_email}
                    onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                    data-testid="email-new-input"
                    className="input-field"
                    placeholder="name@example.com"
                  />
                </div>
                <div>
                  <label className="label-elegant block mb-2" htmlFor="email-password-input">
                    Confirm With Current Password
                  </label>
                  <PasswordInput
                    id="email-password-input"
                    autoComplete="current-password"
                    value={emailForm.current_password}
                    onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                    data-testid="email-password-input"
                    className="input-field"
                    placeholder="Your password"
                  />
                  <p className="text-xs text-[#7A7A7A] mt-2">
                    For your security, we ask for your password before changing the email on your account.
                  </p>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={cancelEmailEdit}
                    data-testid="email-cancel"
                    className="btn-secondary text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEmail}
                    disabled={emailBusy}
                    data-testid="email-save"
                    className="btn-primary"
                  >
                    {emailBusy ? "Updating…" : "Update Email"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <dt className="label-elegant">Email</dt>
                <dd className="text-[#1A1A1A] mt-1" data-testid="email-display">{user.email}</dd>
              </div>
            )}
          </div>
        )}

        {/* Non-member (pro shop) — show email read-only on its own row */}
        {!isMember && (
          <div className="surface-card p-8">
            <div className="overline">Sign-In</div>
            <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Email Address</h2>
            <div className="mt-4 text-[#1A1A1A]">{user.email}</div>
          </div>
        )}

        {isMember && <EmailPreferencesCard user={user} onSaved={setUser} />}

        {isMember && <ClubsEditor user={user} onSaved={setUser} />}
      </div>
    </div>
  );
}
